/**
 * PERMISSIONS UTILITY
 *
 * Centralized permission checking for role-based access control.
 * The admin list is populated from the backend API (/api/admin-users) at runtime.
 * Call setAdminList() once after fetching admin users on login.
 */

// Runtime admin list — populated from backend, never hardcoded
let runtimeAdminList = [];

/**
 * Update the in-memory admin list from the backend response
 * @param {string[]} admins - Array of lowercase email strings
 */
export const setAdminList = (admins) => {
    runtimeAdminList = (admins || []).map(e => e.toLowerCase());
};

/**
 * Check if a user email is an admin
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user is admin
 */
export const isAdmin = (userEmail) => {
    if (!userEmail) return false;
    return runtimeAdminList.includes(userEmail.toLowerCase());
};

/**
 * Check if user can delete teams/groups
 * Only admins can delete (destructive operation)
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can delete
 */
export const canDeleteTeams = (userEmail) => {
    return isAdmin(userEmail);
};

/**
 * Check if user can create teams/groups
 * All authenticated users can create (with full audit logging)
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can create
 */
export const canCreateTeams = (userEmail) => {
    return Boolean(userEmail);
};

/**
 * Check if user can edit teams/groups
 * All authenticated users can edit (with full audit logging)
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can edit
 */
export const canEditTeams = (userEmail) => {
    return Boolean(userEmail);
};

/**
 * Check if user can manage API keys
 * All authenticated users can manage API keys (with full audit logging)
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can manage API keys
 */
export const canManageApiKeys = (userEmail) => {
    return Boolean(userEmail);
};

/**
 * Check if user can access system settings
 * Only admins can access system settings
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can access system settings
 */
export const canAccessSystemSettings = (userEmail) => {
    return isAdmin(userEmail);
};

/**
 * Check if user can manage user roles
 * Only admins can promote/demote users
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can manage roles
 */
export const canManageUserRoles = (userEmail) => {
    return isAdmin(userEmail);
};

/**
 * Check if user can view audit logs
 * All authenticated users can view audit logs
 * @param {string} userEmail - User's email address
 * @returns {boolean} True if user can view audit logs
 */
export const canViewAuditLogs = (userEmail) => {
    return Boolean(userEmail);
};

/**
 * Get user's permission summary
 * @param {string} userEmail - User's email address
 * @returns {Object} Object containing all permissions
 */
export const getUserPermissions = (userEmail) => {
    const admin = isAdmin(userEmail);

    return {
        isAdmin: admin,
        canCreate: canCreateTeams(userEmail),
        canEdit: canEditTeams(userEmail),
        canDelete: canDeleteTeams(userEmail),
        canManageKeys: canManageApiKeys(userEmail),
        canAccessSystemSettings: canAccessSystemSettings(userEmail),
        canManageRoles: canManageUserRoles(userEmail),
        canViewAuditLogs: canViewAuditLogs(userEmail)
    };
};
